import { stripChatMarkdown } from "@components/chat/chat_markdown";
import type { CcThread } from "@stores/customer_care";
import { chatSubjectOf, chatTitleOf } from "@utilities/chat-title";
import {
  ccAttachmentUrl,
  type CcConversation,
  type CcMessage,
  type CcUser,
} from "@utilities/customer-care";

/** The brand the chat greets and signs with (`{appName}` in chat_greeting,
 *  chat_greeting_name, chat_support_team, chat_new_conversation). */
export const CHAT_BRAND = { appName: process.env.GATSBY_APP_NAME ?? "" };

/** The chat's 32px icon buttons (close, refresh, back, attach, remove), with
 *  a 44px touch area around them. */
export const CHAT_ICON_BUTTON =
  "relative flex size-8 shrink-0 items-center justify-center rounded-full text-black/70 transition-colors duration-150 after:absolute after:-inset-1.5 hover:bg-black/5 hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 disabled:opacity-40 [&>svg]:size-5";

/** A reply choice or popular topic as a bubble (button chips and thread).
 *  Opaque in every state: the button's chips float over the page. */
export const CHAT_CHIP =
  "rounded-full border border-primary-200 bg-white px-4 py-2.5 text-sm font-semibold text-primary-700 shadow-sm transition duration-150 hover:bg-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 active:scale-95 disabled:opacity-60";

/** Popular topics (Figma quick-reply chips, capped at five). */
export const CHAT_TOPICS: Array<{ icon: string; key: string }> = [
  { icon: "sim", key: "chat_topic_setup" },
  { icon: "globe", key: "chat_topic_signal" },
  { icon: "billing", key: "chat_topic_billing" },
  { icon: "transfer", key: "chat_topic_transfer" },
  { icon: "plan", key: "chat_topic_plan" },
];

/** A conversation is closed when its `workflowStatus` is "closed" (Luca,
 *  2026-09-29); the rating card shows only then. */
export const isClosed = (conversation: CcConversation | undefined): boolean =>
  conversation?.workflowStatus?.toLowerCase() === "closed";

export interface Sender {
  mine: boolean;
  isBot: boolean;
  name: string;
  /** The sender's profile image, when the API gives one. */
  image?: string;
  initial: string;
  /** Avatar tint: bot in the primary colour, people in the secondary. */
  tone: string;
}

const firstNameOf = (user: CcUser | undefined): string => {
  const firstName = (user?.firstName ?? "").trim();
  return firstName !== "" ? firstName : (user?.email ?? "").split("@")[0];
};

export const senderOf = (
  message: CcMessage,
  thread: CcThread | undefined,
  profileId: string | number | undefined,
  fallbackName: string,
): Sender => {
  const mine =
    profileId !== undefined && String(message.userId) === String(profileId);
  const user = thread?.senders[String(message.userId)];
  const isBot = (user?.userType ?? "").toLowerCase() === "bot";
  const known = firstNameOf(user);
  const name = known !== "" ? known : fallbackName;
  const image = (user?.profileImage ?? "").trim();
  return {
    mine,
    isBot,
    name,
    image: image === "" ? undefined : ccAttachmentUrl(image),
    initial: name.charAt(0).toUpperCase(),
    tone: isBot
      ? "bg-primary/10 text-primary-700"
      : "bg-secondary/15 text-black",
  };
};

export const agentNameOf = (
  conversation: CcConversation,
  fallbackName: string,
): string => {
  const name = firstNameOf(conversation.agent ?? undefined);
  return name !== "" ? name : fallbackName;
};

export const previewOf = (
  conversation: CcConversation,
  attachmentLabel: string,
): string => {
  const last = conversation.lastMessage;
  if (last === null || last === undefined) return conversation.title ?? "";
  if (last.message.trim() !== "") {
    const { text } = splitSuggestions(last.message);
    return oneLine(stripChatMarkdown(text !== "" ? text : last.message));
  }
  const first = last.attachments?.[0];
  return first !== undefined ? first[0] : attachmentLabel;
};

/** A message as one card line: list markers ("- ", "1. ") dropped, line
 * breaks as spaces. */
export const oneLine = (text: string): string =>
  text
    .split("\n")
    .map(line => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, "").trim())
    .filter(line => line !== "")
    .join(" ");

export const formatTime = (iso: string, locale: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale === "kr" ? "ko" : locale, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

export const isToday = (iso: string): boolean => {
  const date = new Date(iso);
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

const isYesterday = (iso: string): boolean => {
  const date = new Date(iso);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate()
  );
};

/** "Oct 6"; the year only for an earlier year ("Oct 6, 2025"). */
export const formatDay = (iso: string, locale: string): string =>
  new Intl.DateTimeFormat(locale === "kr" ? "ko" : locale, {
    day: "numeric",
    month: "short",
    year:
      new Date(iso).getFullYear() === new Date().getFullYear()
        ? undefined
        : "numeric",
  }).format(new Date(iso));

/** Card time: the clock today, "Yesterday" (the browser's own word for it
 * in each language), the date otherwise. */
export const formatWhen = (iso: string, locale: string): string => {
  if (isToday(iso)) return formatTime(iso, locale);
  if (isYesterday(iso)) {
    const tag = locale === "kr" ? "ko" : locale;
    const word = new Intl.RelativeTimeFormat(tag, { numeric: "auto" }).format(
      -1,
      "day",
    );
    return word.charAt(0).toLocaleUpperCase(tag) + word.slice(1);
  }
  return formatDay(iso, locale);
};

/**
 * Reply choices a bot/agent message offers (the design's quick-reply chips
 * under the latest message). The CC message `payload` is undocumented, so
 * every likely shape is accepted: an array of strings, an array of objects
 * with `label` / `title` / `text` / `value`, or an object holding such an
 * array under `quickReplies`, `quick_replies`, `replies`, `options`,
 * `buttons` or `choices`. To confirm with Luca.
 */
export const quickRepliesOf = (payload: unknown): string[] => {
  const asList = (value: unknown): string[] =>
    Array.isArray(value)
      ? value
          .map(item =>
            typeof item === "string"
              ? item
              : item !== null && typeof item === "object"
                ? String(
                    (item as Record<string, unknown>).label ??
                      (item as Record<string, unknown>).title ??
                      (item as Record<string, unknown>).text ??
                      (item as Record<string, unknown>).value ??
                      "",
                  )
                : "",
          )
          .filter(text => text.trim() !== "")
      : [];
  if (typeof payload === "string") {
    try {
      return quickRepliesOf(JSON.parse(payload));
    } catch {
      return [];
    }
  }
  if (Array.isArray(payload)) return asList(payload);
  if (payload === null || typeof payload !== "object") return [];
  const record = payload as Record<string, unknown>;
  if (record.data !== undefined && record.data !== payload) {
    const nested = quickRepliesOf(record.data);
    if (nested.length > 0) return nested;
  }
  for (const key of [
    "quickReplies",
    "quick_replies",
    "replies",
    "options",
    "buttons",
    "choices",
  ]) {
    const list = asList(record[key]);
    if (list.length > 0) return list;
  }
  return [];
};

/**
 * The bot writes its reply choices inline, as bracketed segments after the
 * text. Either one segment per choice ("… [Set up my eSIM] [Billing]") or
 * one segment with a "Suggestions:" prefix and pipes between the choices
 * ("… [Suggestions: Thailand, 5GB, 30 days | I need unlimited data]"). The
 * app turns each choice into a bubble; so does the website. Returns the text
 * without the segments and the choices in order.
 */
export const splitSuggestions = (
  message: string,
): { text: string; suggestions: string[] } => {
  const suggestions: string[] = [];
  const add = (label: string): void => {
    const trimmed = label.trim();
    if (trimmed !== "" && !suggestions.includes(trimmed)) {
      suggestions.push(trimmed);
    }
  };
  const text = message
    .replace(/\[([^[\]]{1,400})\]/g, (_match, segment: string) => {
      const body = segment.replace(/^\s*suggestions?\s*:\s*/i, "");
      body.split("|").forEach(add);
      return "";
    })
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return { text, suggestions };
};

/** The sender id of a message, whichever field the API used for it. */
export const senderIdOf = (
  message: CcMessage | null | undefined,
): string | undefined => {
  if (message === null || message === undefined) return undefined;
  const raw = message as unknown as Record<string, unknown>;
  const id =
    raw.userId ?? raw.user_id ?? raw.senderId ?? raw.sender_id ?? raw.from;
  return id === undefined || id === null ? undefined : String(id);
};

/**
 * What the customer's words name a chat: the fixed title of the first common
 * subject they mention (chatSubjectOf: "Is there a way to get refund" →
 * "Refund"), else the tidied first sentence (chatTitleOf). "" when only a
 * greeting is left.
 */
export const nameFrom = (
  text: string | null | undefined,
  locale: string,
  t: (key: string) => string,
): string => {
  const subject = chatSubjectOf(text);
  return subject !== undefined ? t(subject) : chatTitleOf(text, locale);
};

/**
 * A conversation's saved title as shown: a popular topic stays as it was
 * picked; a typed one is named again by nameFrom, so chats named before
 * 2026-10-12 read the same as new ones.
 */
export const savedTitleOf = (
  title: string | null | undefined,
  locale: string,
  t: (key: string) => string,
): string => {
  const saved = (title ?? "").trim();
  return CHAT_TOPICS.some(topic => t(topic.key) === saved)
    ? saved
    : nameFrom(saved, locale, t);
};

/**
 * What names a chat that has no title (chats from before titles existed, or
 * opened with just "Hi"): the first of the customer's messages that says
 * something, named the way a typed first message names a new chat
 * (nameFrom). "" until the detail is loaded. Used by the list card and the
 * conversation header so both read the same.
 */
export const customerTitleOf = (
  messages: CcMessage[] | undefined,
  profileId: string | number | undefined,
  locale: string,
  t: (key: string) => string,
): string => {
  if (profileId === undefined || messages === undefined) return "";
  for (const message of messages) {
    if (senderIdOf(message) !== String(profileId)) continue;
    const title = nameFrom(message.message, locale, t);
    if (title !== "") return title;
  }
  return "";
};
